import React from "react";
import { Box, DialogContent, Paper, Tooltip, useTheme } from "@mui/material";
import { ReactNode, useState } from "react";
import { mergeQueries, Query, queryOps } from "system/query/Query";
import { DndContext, DragStartEvent, DragEndEvent, DragOverlay, useSensors, useSensor, PointerSensor } from '@dnd-kit/core';
import QuerySegmentContainer from "./queryBuilder/common/QuerySegmentContainer";
import { QueryInfo } from "system/query/QueryInfo";
import { QueryBuilderAddButton } from "./queryBuilder/common/QueryBuilderAddButton";
import { QueryBuilderAddButtonDraggable } from "./queryBuilder/common/QueryBuilderAddButtonDraggable";
import { enforceBranch, findQueryPartByPath, getDefaultQuerySegmentsByOp, getSegmentAsCorrectType, queryOpIsBranch, segmentIsBranch } from "system/query/QueryUtility";
import { QuerySegment } from "system/query/QuerySegment";
import { QueryBranchBuilderDialog } from "./queryBuilder/segments/branch/QueryBranchBuilderDialog";
import { QueryBranch } from "system/query/QueryBranch";
import { QueryLeafNodeBuilderDialog } from "./queryBuilder/segments/leaf/QueryLeafNodeBuilderDialog";
import { QueryLeafNode } from "system/query/QueryLeafNode";
import QueryBuilderAddPanel from "./queryBuilder/common/QueryBuilderAddPanel";
import { QuerySpan } from "system/query/QuerySpan";
import { AIQueryBuilderDialog } from "./queryBuilder/common/AIQueryBuilderDialog";
import { useTranslation } from "react-i18next";
import QueryQuickStartDialog from "./QueryQuickStartDialog";
import { permissionTest } from "system/InfegyUser";
import { QuerySpanBuilderDialog } from "./queryBuilder/segments/span/QuerySpanBuilderDialog";
import AIGradientButton from "components/design/AI/AIGradientButton";

export interface QueryBuilderPanelProps {
  query: Query;
  queryInfo: QueryInfo;
  setQuery?: (query: Query) => void;
  size?: "medium" | "small";
  children?: ReactNode;
}

export interface QueryBuilderPanelState {
  draggingIsDisabled: boolean;
  setDraggingIsDisabled: (disabled: boolean) => void;
}

export const BuilderContext = React.createContext<QueryBuilderPanelState>({
  draggingIsDisabled: false,
  setDraggingIsDisabled: () => {}
});
export const BuilderProvider = BuilderContext.Provider;

export interface SegmentDialogProps {
  segment: QuerySegment;
  parent: QuerySegment;
  inSpan?: boolean;
  spanSide?: string;
}

export interface AddPanelOptions {
  parentBranch?: QueryBranch | QuerySpan;
  inSpan?: boolean;
  spanSide?: string;
}

export default function QueryBuilderPanel(props: QueryBuilderPanelProps): JSX.Element {
  const query:Query = props.query;
  const theme = useTheme();
  const { i18n } = useTranslation();

  const sidebarWidth = "185px",
      topbarHeight = props.size === "small" ? "74.5px" : "117px",
      bottombarHeight = "59px";

  const [draggingIsDisabled, setDraggingIsDisabled] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [segmentDialogProps, setSegmentDialogProps] = useState<SegmentDialogProps | undefined>(undefined);
  const [addSegmentDialogOptions, setAddSegmentDialogOptions] = useState<AddPanelOptions | undefined>(undefined);
  const [quickStartDialogIsOpen, setQuickStartDialogIsOpen] = useState(false);
  const [aiDialogIsOpen, setAiDialogIsOpen] = useState(false);

  const updateQuery = function(newQuery?:Query) {
    if (!newQuery) {
      newQuery = query;
    }
    props.setQuery && props.setQuery({...newQuery} as Query);
  }

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 10,
      },
    })
  )

  function handleDragStart(event: DragStartEvent) {
    const id = event.active.id as string;
    if (id.startsWith('op-')) {
      setActiveId(id);
    }
  }

  function handleDragEnd(event: DragEndEvent) {
    const [foundPart, parent, inSpan, spanSideValue] = findQueryPartByPath(query, event.over?.id as string);
    if (!foundPart) {
      return;
    }
    const targetPart:any = getSegmentAsCorrectType(foundPart);
    const activeId: string | null = event.active?.id as string;

    if (activeId === event.over?.id) {
      return;
    }

    if (queryOpIsBranch(targetPart.op) && !targetPart.values) {
      targetPart.values = [];
    }

    if (activeId?.startsWith('op')) {
      const op = activeId?.slice(3);
      if (op === "null") {
        const defaultSegment:QueryLeafNode | QueryBranch | QuerySpan = new QueryLeafNode({op:undefined});
        targetPart.values.push(defaultSegment);
      } else if (op && queryOpIsBranch(targetPart.op)) {
        const defaultSegment:QueryBranch | QueryLeafNode | QuerySpan | undefined = getDefaultQuerySegmentsByOp(op);
        targetPart.values.push(defaultSegment);
      }
      return;
    }

    let wasAdded:boolean = false;

    const [droppedPart, droppedParent, droppedInSpan, droppedSide] = findQueryPartByPath(query, event.active?.id as string);

    if (queryOpIsBranch(targetPart.op) && targetPart.values) {
      targetPart.values.push(droppedPart);
      wasAdded = true;
    }

    // Handle spans
    if (targetPart.op === queryOps.span && inSpan) {
      if (spanSideValue === "left") {
        if (targetPart.left) {
          if (queryOpIsBranch(targetPart.left.op)) {
            if (!targetPart.left.values) {
              targetPart.left.values = [];
            }
            targetPart.left.values.push(droppedPart);
            wasAdded = true;
          } else {
            targetPart.left = {op:queryOps.and, values:[targetPart.left, droppedPart]} as QueryBranch;
            wasAdded = true;
          }
        } else {
          targetPart.left = droppedPart;
          wasAdded = true;
        }
      } else {
        if (targetPart.right) {
          if (queryOpIsBranch(targetPart.right.op)) {
            if (!targetPart.right.values) {
              targetPart.right.values = [];
            }
            targetPart.right.values.push(droppedPart);
            wasAdded = true;
          } else {
            targetPart.right = {op:queryOps.and, values:[targetPart.right, droppedPart]} as QueryBranch;
            wasAdded = true;
          }
        } else {
          targetPart.right = droppedPart;
          wasAdded = true;
        }
      }
    }

    if (droppedParent && wasAdded) {
      const droppedParentPart:any = getSegmentAsCorrectType(droppedParent);
      if (droppedParent.op !== queryOps.span && droppedParentPart && droppedParentPart.values) {
        let found = false;
        droppedParentPart.values = droppedParentPart.values.filter((value:any) => {
          if (value === droppedPart && !found) {
            found = true;
            return false;
          }
          return true;
        });
      }
    }

    setActiveId(null);
  }

  function addContainerSegment(op:string, parentBranch?:QueryBranch | QuerySpan, inSpan?:boolean, spanSide?:string) {
    if (!query.query) {
      return;
    }
    if (!parentBranch) {
      query.query = enforceBranch(query.query);
      parentBranch = query.query as QueryBranch;
    }

    let branchArray;
    if (parentBranch.op === queryOps.span && inSpan && spanSide) {
      if (spanSide === "left") {
        if ((parentBranch as QuerySpan).left && !segmentIsBranch((parentBranch as QuerySpan).left)) {
          (parentBranch as QuerySpan).left = enforceBranch((parentBranch as QuerySpan).left);
        }
        branchArray = (parentBranch as QuerySpan).left;
      } else {
        if ((parentBranch as QuerySpan).right && !segmentIsBranch((parentBranch as QuerySpan).right)) {
          (parentBranch as QuerySpan).right = enforceBranch((parentBranch as QuerySpan).right);
        }
        branchArray = (parentBranch as QuerySpan).right;
      }
    } else {
      if (!(parentBranch as QueryBranch).values) {
        (parentBranch as QueryBranch).values = [];
      }
      branchArray = (parentBranch as QueryBranch).values;
    }

    const defaultSegment:any = getDefaultQuerySegmentsByOp(op);
    if (branchArray) {
      branchArray.push(defaultSegment);
    } else if (parentBranch.op === queryOps.span && inSpan && spanSide === "left") {
      (parentBranch as QuerySpan).left = defaultSegment;
    } else if (parentBranch.op === queryOps.span && inSpan && spanSide === "right") {
      (parentBranch as QuerySpan).right = defaultSegment;
    }
    updateQuery();
  }

  function addLeafSegment(op?:string, parentBranch?:QueryBranch | QuerySpan, inSpan?:boolean, spanSide?:string) {
    if (!query.query) {
      return;
    }
    if (!parentBranch) {
      query.query = enforceBranch(query.query);
      parentBranch = query.query as QueryBranch;
    }

    let branchArray;
    if (parentBranch.op === queryOps.span && inSpan && spanSide) {
      if (spanSide === "left") {
        if ((parentBranch as QuerySpan).left && !segmentIsBranch((parentBranch as QuerySpan).left)) {
          (parentBranch as QuerySpan).left = enforceBranch((parentBranch as QuerySpan).left);
        }
        branchArray = (parentBranch as QuerySpan).left;
      } else {
        if ((parentBranch as QuerySpan).right && !segmentIsBranch((parentBranch as QuerySpan).right)) {
          (parentBranch as QuerySpan).right = enforceBranch((parentBranch as QuerySpan).right);
        }
        branchArray = (parentBranch as QuerySpan).right;
      }
    } else {
      if (!(parentBranch as QueryBranch).values) {
        (parentBranch as QueryBranch).values = [];
      }
      branchArray = (parentBranch as QueryBranch).values;
    }
    let defaultSegment:QueryLeafNode | QueryBranch | QuerySpan = new QueryLeafNode({op:op});
    if (op) {
      const newSegment = getDefaultQuerySegmentsByOp(op);
      if (newSegment) {
        defaultSegment = newSegment;
      }
    }
    if (branchArray) {
      branchArray.push(defaultSegment);
    } else if (parentBranch.op === queryOps.span && inSpan && spanSide === "left") {
      (parentBranch as QuerySpan).left = defaultSegment;
    } else if (parentBranch.op === queryOps.span && inSpan && spanSide === "right") {
      (parentBranch as QuerySpan).right = defaultSegment;
    }
    updateQuery();
    editSegment(defaultSegment, parentBranch, inSpan, spanSide);
  }

  function editSegment(segment:QuerySegment, parent:QuerySegment | undefined, inSpan?:boolean, spanSide?:string) {  
    if (!segment || !parent) {
      setSegmentDialogProps(undefined);
      return;
    }
    setSegmentDialogProps({segment: segment, parent: parent, inSpan: inSpan, spanSide: spanSide});
  }

  function addSegment(parent:QuerySegment | Query | undefined, inSpan?:boolean, spanSide?:string) {
    setAddSegmentDialogOptions({parentBranch: parent as QueryBranch | QuerySpan, inSpan: inSpan, spanSide: spanSide});
  }

  const segOp = segmentDialogProps?.segment?.op;

  const branchSegmentDialogIsOpen = segOp === queryOps.and || segOp === queryOps.or || segOp === queryOps.not;
  const spanSegmentDialogIsOpen = segOp === queryOps.span;
  const leafSegmentDialogIsOpen = segmentDialogProps?.segment && !branchSegmentDialogIsOpen && !spanSegmentDialogIsOpen;

  const hasAIPermission = permissionTest("ai_features");

  let activeDropOp: string | null | undefined= activeId?.slice(3);
  if (activeDropOp === "null" || activeDropOp === undefined) {
    activeDropOp = null;
  }

  return (
  <BuilderProvider value={{draggingIsDisabled, setDraggingIsDisabled}}>
  <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
    <DialogContent dividers  sx={{ p:0.5, pl: 0, minHeight:"40vh", height: "70vh", position:'relative', backgroundColor: theme.palette.background.default }} >
      <Box sx={{margin:`0 0 0 ${sidebarWidth}`, overflow:"auto", right:"0", minHeight:"450px", p:1}}>
        {query.query &&
          <QuerySegmentContainer
            depth={0}
            path=""
            queryInfo={props.queryInfo}
            parent={query.query}
            editSegment={editSegment}
            addSegment={addSegment}
            querySegment={query.query} />
        }
      </Box>
      {activeId &&
        <DragOverlay>
          <QueryBuilderAddButton op={activeDropOp as queryOps} onClick={() => {}} />
        </DragOverlay>
      }
    </DialogContent>
    <Box
      sx={{m:0, p:1.5, pb:10, pr: 1,
        position:"absolute",
        overflowX:"visible",
        textAlign:"center",
        top:topbarHeight, bottom:bottombarHeight, width:sidebarWidth,
        overflowY:"auto", backgroundColor:theme.palette.background.default,
        borderTop: `1px solid ${theme.palette.divider}`,
        borderBottom: `1px solid ${theme.palette.divider}`,
        "&.MuiBox-root:focus-visible": {
          outline: "none"
        },
        "&.MuiBox-root > *:focus-visible": {
          outline: "none"
        }
      }}
    >
      <Paper sx={{pt: 1, pb:1}}>
      <Tooltip
        title={hasAIPermission ? i18n.t("ai-query.dialog-title") : i18n.t("ai-query.not-allowed")}
        followCursor>
        <Box sx={{cursor: "pointer", marginBottom: "10px", display: "block", p: 1}}>
        <AIGradientButton
          disabled={!hasAIPermission}
          additionalStyles={{width: "100%"}}
          onClick={() => {
            setAiDialogIsOpen(true);
          }}>
          {i18n.t("ai-query.ai-assist")}
        </AIGradientButton>
        </Box>
      </Tooltip>
      <QueryBuilderAddButtonDraggable onClick={addLeafSegment}/>
      </Paper>
      <Paper sx={{mt: 2, pt: 1, pb:1}}>
      {i18n.t("query-form.add-group")}
      <QueryBuilderAddButtonDraggable op={queryOps.and} onClick={() => {
        addContainerSegment(queryOps.and);
      }} />
      <QueryBuilderAddButtonDraggable op={queryOps.or} onClick={() => {
        addContainerSegment(queryOps.or);
      }} />
      <QueryBuilderAddButtonDraggable op={queryOps.not} onClick={() => {
        addContainerSegment(queryOps.not);
      }} />
      <QueryBuilderAddButtonDraggable op={queryOps.span} onClick={() => {
        addLeafSegment(queryOps.span);
      }} />
      </Paper>
      <Paper sx={{mt: 2, pt: 1, pb:1}}>
        {i18n.t("query-form.quick-filters")}
        <QueryBuilderAddButton onClick={() => { setQuickStartDialogIsOpen(true); }} labelOverride={i18n.t("query-form.quick-start")} />
        {i18n.t("query-form.add-op")}
        <QueryBuilderAddButtonDraggable op={queryOps.boolean} onClick={() => {
          addLeafSegment(queryOps.boolean);
        }} />
      </Paper>
    </Box>
    {branchSegmentDialogIsOpen &&
      <QueryBranchBuilderDialog
        isOpen={true}
        setDialogIsOpen={setSegmentDialogProps}
        parent={segmentDialogProps?.parent}
        queryBranch={segmentDialogProps?.segment as QueryBranch}
        queryInfo={props.queryInfo}
        updateQuery={updateQuery}
        originalQuery={query} />
    }
    {spanSegmentDialogIsOpen &&
      <QuerySpanBuilderDialog
        isOpen={true}
        setDialogIsOpen={setSegmentDialogProps}
        parent={segmentDialogProps?.parent}
        querySpan={segmentDialogProps?.segment as QuerySpan}
        queryInfo={props.queryInfo}
        updateQuery={updateQuery}
        originalQuery={query} />
    }
    {leafSegmentDialogIsOpen &&
      <QueryLeafNodeBuilderDialog
        isOpen={true}
        setDialogIsOpen={setSegmentDialogProps}
        parent={segmentDialogProps?.parent}
        queryLeafNode={segmentDialogProps?.segment as QueryLeafNode}
        queryInfo={props.queryInfo}
        updateQuery={updateQuery}
        inSpan={segmentDialogProps?.inSpan}
        spanSide={segmentDialogProps?.spanSide}
        originalQuery={query} />
    }
    {!!addSegmentDialogOptions &&
      <QueryBuilderAddPanel
        isOpen={!!addSegmentDialogOptions}
        onClose={() => setAddSegmentDialogOptions(undefined)}
        addLeafSegment={addLeafSegment}
        addContainerSegment={addContainerSegment}
        parentBranch={addSegmentDialogOptions.parentBranch}
        inSpan={addSegmentDialogOptions.inSpan}
        spanSide={addSegmentDialogOptions.spanSide}
        onSelection={(op:queryOps | undefined) => {
          const parentBranch = addSegmentDialogOptions?.parentBranch as QueryBranch;
          if (!parentBranch) {
            return;
          }
          if (!parentBranch.values) {
            parentBranch.values = [];
          }
          const defaultSegment:any = getDefaultQuerySegmentsByOp(op);
          parentBranch.values.push(defaultSegment);
          updateQuery();
          setAddSegmentDialogOptions(undefined);
          editSegment(defaultSegment, parentBranch);
        }}/>
    }
    {aiDialogIsOpen &&
      <AIQueryBuilderDialog
        queryInfo={props.queryInfo}
        onClose={() => setAiDialogIsOpen(false)}
        onReplaceQuery={(newQuery:Query) => {
          updateQuery(newQuery);
          setAiDialogIsOpen(false);
        }}
        onInsertQuery={(newQuery:Query) => {
          const mergedQuery = mergeQueries([query, newQuery], {flatten: true});
          mergedQuery.prompt = newQuery.prompt;
          updateQuery(mergedQuery);
          setAiDialogIsOpen(false);
        }}
      />
    }
    {quickStartDialogIsOpen &&
      <QueryQuickStartDialog
        queryInfo={props.queryInfo}
        onClose={() => setQuickStartDialogIsOpen(false)}
        onReplaceQuery={(query:Query) => {
          updateQuery(query);
          setQuickStartDialogIsOpen(false);
        }}
        onInsertQuery={(newQuery:Query) => {
          const mergedQuery = mergeQueries([query, newQuery], {flatten: true});
          updateQuery(mergedQuery);
          setQuickStartDialogIsOpen(false);
        }}
      />
    }
  </DndContext>
  </BuilderProvider>
  );
}